import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null)
  const [portalToken, setPortalToken] = useState(null)
  const [userType, setUserType] = useState(null)
  const [role, setRole] = useState(null)
  const [studentIds, setStudentIds] = useState([])
  const [classIds, setClassIds] = useState([])
  const [entityId, setEntityId] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    async function initAuth() {
      try {
        const stored = sessionStorage.getItem('portal-auth')
        if (stored && mounted) {
          const parsed = JSON.parse(stored)
          const parsedStudentIds = Array.isArray(parsed.studentIds) ? parsed.studentIds : []
          const parsedClassIds = Array.isArray(parsed.classIds) ? parsed.classIds : []
          const parsedRole = typeof parsed.role === 'string'
            ? parsed.role
            : (parsed.userType === 'student' ? 'student' : null)
          const hasScope = parsedRole === 'ta' || parsedStudentIds.length > 0

          if (parsed.userType && parsed.portalToken && hasScope) {
            setSession({ validated: true, token: parsed.portalToken })
            setUserType(parsed.userType)
            setRole(parsedRole)
            setStudentIds(parsedStudentIds)
            setClassIds(parsedClassIds)
            setEntityId(parsed.entityId)
            setPortalToken(parsed.portalToken)
          }
        }
      } catch {
        // sessionStorage unavailable or corrupt
      } finally {
        if (mounted) setLoading(false)
      }
    }

    initAuth()
    return () => {
      mounted = false
    }
  }, [])

  const setAuthData = useCallback(({
    userType: ut,
    role: nextRole,
    studentIds: sids,
    classIds: cids,
    entityId: eid,
    portalToken: pt,
  }) => {
    setUserType(ut)
    const resolvedRole = nextRole || (ut === 'student' ? 'student' : null)
    setRole(resolvedRole)
    setStudentIds(sids || [])
    setClassIds(cids || [])
    setEntityId(eid)
    setPortalToken(pt || null)
    try {
      sessionStorage.setItem('portal-auth', JSON.stringify({
        userType: ut,
        role: resolvedRole,
        studentIds: sids || [],
        classIds: cids || [],
        entityId: eid,
        portalToken: pt || null,
      }))
    } catch {
      // sessionStorage unavailable
    }
  }, [])

  const signOut = useCallback(async () => {
    // Portal auth is token-based (portal_tokens table, validated via the
    // validate-portal-token Edge Function). N7B phase 8 (2026-04-20)
    // retired the Supabase auth signOut call — there's no Supabase session
    // to invalidate, so sign-out is a pure client-state reset.
    setSession(null)
    setPortalToken(null)
    setUserType(null)
    setRole(null)
    setStudentIds([])
    setClassIds([])
    setEntityId(null)
    try { sessionStorage.removeItem('portal-auth') } catch {}
  }, [])

  const isAuthenticated = Boolean(
    session &&
    portalToken &&
    userType &&
    (role === 'ta' || studentIds.length > 0)
  )

  const value = useMemo(() => ({
    session,
    portalToken,
    userType,
    role,
    studentIds,
    classIds,
    entityId,
    isAuthenticated,
    loading,
    setSession,
    setAuthData,
    signOut,
  }), [
    session,
    portalToken,
    userType,
    role,
    studentIds,
    classIds,
    entityId,
    isAuthenticated,
    loading,
    setAuthData,
    signOut,
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
